import { useApolloClient } from "@apollo/client";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  assert,
  assertNever,
  getDataOrNull,
  getNameWithCopy,
  headOrThrow,
  throwIfUndefined,
} from "@msys/common";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  ListHeader,
  LoadingSpinner,
  MediaCardItem,
  MediaListItem,
  MenuButton,
  Modal,
  ModalOpenButton,
  ModalOpenProcess,
  SplitButton,
  Tabs,
  TagChips,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import {
  BookmarkAdd as BookmarkAddIcon,
  BookmarkRemove as BookmarkRemoveIcon,
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  ControlPointDuplicate as ControlPointDuplicateIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon,
  Publish as PublishIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  ButtonProps,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { DepGraph } from "dependency-graph";
import { Form, Formik, useFormik } from "formik";
import { keyBy, partition } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  namedOperations,
  OrganisationQuoteTemplatesFilters,
  OrganisationQuoteTemplatesSortBy,
  OrganisationQuoteTemplatesSorting,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { ViewerShapeFragment } from "../../../auth/UserDataProvider.generated.js";
import { useUserData } from "../../../auth/useUserData.js";
import { download } from "../../../browserUtils.js";
import { SwitchCollectionViewButton } from "../../../commons/button/SwitchCollectionViewButton.js";
import EmptyScreen from "../../../commons/EmptyScreen.js";
import { FilterButton } from "../../../commons/filters/FilterButton.js";
import { TagsField } from "../../../commons/form-fields/TagsField.js";
import { useAutoSave } from "../../../commons/hooks/useAutoSave.js";
import { useCardsNumber } from "../../../commons/hooks/useCardsNumber.js";
import { CollectionView } from "../../../commons/hooks/useCollectionView.js";
import { useStateWithUrlParam } from "../../../commons/hooks/useStateWithUrlParam.js";
import { useStateWithUrlParams } from "../../../commons/hooks/useStateWithUrlParams.js";
import { Page, PageTopbarItem } from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { PaginationModel } from "../../../commons/pagination/usePaginationParams.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../../constants.js";
import { CategorySearchModal } from "../../../features/categories/CategorySearchModal.js";
import { usePropertyHelpers } from "../../../features/doc-items/usePropertyHelpers.js";
import { useJsonExport } from "../../../features/jsonImportExport/useJsonExport.js";
import { useOrganisationTags } from "../../../features/organisations/useOrganisationTags.js";
import { useProjectTypes } from "../../../features/projects/useProjectTypes.js";
import { PropertyValueChips } from "../../../features/properties/PropertyValueChips.js";
import { ImportTemplatesMenuItem } from "../../../features/templates/buttons/ImportTemplatesMenuItem.js";
import { useTemplateBulkUpdateProductsMutation } from "../../../features/templates/buttons/TemplateBulkUpdateProductsButton.generated.js";
import {
  templateFiltersDefaultValue,
  TemplateFiltersFormValues,
  templateFiltersValidationSchema,
} from "../../../features/templates/filters/TemplateFilterFields.js";
import { TemplateFilters } from "../../../features/templates/filters/TemplateFilters.js";
import {
  computeTemplateRelevantProperties,
  quoteTemplateSortingToSearchQuotesSorting,
} from "../../../features/templates/helpers.js";
import { CreateQuoteTemplateButton } from "../../../features/templates/quote/buttons/CreateTemplateQuoteButton.js";
import { usePublishQuoteTemplateMutation } from "../../../features/templates/quote/buttons/QuoteTemplatePublishButton.generated.js";
import { useQuoteTemplatePublish } from "../../../features/templates/quote/buttons/QuoteTemplatePublishButton.js";
import { QuoteTemplatesShareModal } from "../../../features/templates/quote/buttons/QuoteTemplateShareButton.js";
import {
  useSubscribeToQuoteTemplate_HasOverridesLazyQuery,
  useSubscribeToQuoteTemplateMutation,
} from "../../../features/templates/quote/buttons/QuoteTemplateSubscribeButton.generated.js";
import { QuoteTemplateSubscribeButton } from "../../../features/templates/quote/buttons/QuoteTemplateSubscribeButton.js";
import { useUnsubscribeFromQuoteTemplateMutation } from "../../../features/templates/quote/buttons/QuoteTemplateUnsubscribeButton.generated.js";
import {
  canUpdateShare,
  useQuoteTemplateUpdateShare,
} from "../../../features/templates/quote/buttons/QuoteTemplateUpdateShareButton.js";
import { ShowTemplateVariantProtocolModal } from "../../../features/templates/quote/ShowTemplateVariantProtocolModal.js";
import { TemplateQuotePublishingStatus } from "../../../features/templates/quote/TemplateQuotePublishingStatus.js";
import {
  useDeleteQuoteTemplateMutation,
  useDuplicateQuoteTemplateMutation,
} from "../../../features/templates/quote/TemplateQuotes.generated.js";
import { useTemplateQuoteStatuses } from "../../../features/templates/quote/TemplateQuoteStatuses.js";
import { useTemplateQuoteWarnings } from "../../../features/templates/quote/TemplateQuoteWarnings.js";
import {
  canShowChips,
  TemplatesQuoteFilterChips,
} from "../../../features/templates/quote/TemplatesQuoteFilterChips.js";
import {
  TemplateQuotesFilterModal,
  TemplateQuotesFilterWithTemplateTypeModal,
} from "../../../features/templates/quote/TemplatesQuoteFilterModal.js";
import {
  useImplementsTemplateTypeMutation,
  useModifyQuoteTemplateMutation,
} from "../../../features/templates/quote/TemplatesQuoteTemplateSettingsModal.generated.js";
import {
  hasTemplateTypeOrPropsInfo,
  TemplateTypeAndPropsInfo,
} from "../../../features/templates/quote/TemplateTypeAndPropsInfo.js";
import { useTemplatesQuoteList } from "../../../features/templates/quote/useTemplatesQuoteList.js";
import { useFetchUpdateProtocolEntries } from "../../../features/templates/quote/useTemplateVariantUpdateProtocol.js";
import { TemplateTypesSearchModal } from "../../../features/templateTypes/TemplateTypesSearchModal.js";
import { useTypeOfTemplates } from "../../../features/templates/quote/useTypeOfTemplates.js";
import { useDataGridStateStore } from "../../../features/users/useDataGridStateStore.js";
import { buildDocPath } from "../../../utils.js";
import {
  TemplateQuotes_TemplateFragment,
  TemplateQuotesQueryVariables,
  useTemplateQuotesQuery,
  useTemplateQuotesSearchQuery,
} from "./TemplateQuotes.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["table", "list", "gallery"];

type SectionValue = "ALL" | "MY" | "MEISTER_STORE";
type QuickFilterValue =
  | "ALL_AVAILABLE"
  | "MY_ALL"
  | "MY_DRAFT"
  | "MY_PRIVATE"
  | "MY_PROTECTED"
  | "MY_PUBLIC"
  | "MEISTER_STORE_ALL"
  | "MEISTER_STORE_SUBSCRIBED";
const QUICK_FILTER_TO_SECTION_MAP: Record<QuickFilterValue, SectionValue> = {
  ALL_AVAILABLE: "ALL",
  MY_ALL: "MY",
  MY_DRAFT: "MY",
  MY_PRIVATE: "MY",
  MY_PROTECTED: "MY",
  MY_PUBLIC: "MY",
  MEISTER_STORE_ALL: "MEISTER_STORE",
  MEISTER_STORE_SUBSCRIBED: "MEISTER_STORE",
};
const SECTION_TO_DEFAULT_QUICK_FILTER: Record<SectionValue, QuickFilterValue> =
  {
    ALL: "ALL_AVAILABLE",
    MY: "MY_ALL",
    MEISTER_STORE: "MEISTER_STORE_ALL",
  };

type Filters = OrganisationQuoteTemplatesFilters;

interface Props {
  submenuItems: PageTopbarItem[];
  section: SectionValue;
  pathToDocList: string;
}

export const TemplateQuotes = ({
  submenuItems,
  section,
  pathToDocList,
}: Props) => {
  const { t } = useTranslate(["Templates", "Global", "TemplatesSharing"]);

  const { isMinTablet } = useScreenWidth();

  const quickFilterSettings: Record<
    QuickFilterValue,
    { label: string; values: Filters; visible?: boolean }
  > = React.useMemo(
    () => ({
      ALL_AVAILABLE: {
        label: t("All", { ns: "Global" }),
        values: {
          sources: ["available"],
        },
      },
      MY_ALL: {
        label: t("All", { ns: "Global" }),
        values: {
          sources: ["mine_published", "mine_unpublished"],
        },
      },
      MY_DRAFT: {
        label: t("Draft", { ns: "Templates" }),
        values: {
          sources: ["mine_unpublished"],
        },
      },
      MY_PRIVATE: {
        label: t("Private", { ns: "TemplatesSharing" }),
        values: {
          sources: ["mine_published"],
          mineShareStatus: "PRIVATE",
        },
      },
      MY_PROTECTED: {
        label: t("Protected", { ns: "TemplatesSharing" }),
        values: {
          sources: ["mine_published"],
          mineShareStatus: "PROTECTED",
        },
      },
      MY_PUBLIC: {
        label: t("Public", { ns: "TemplatesSharing" }),
        values: {
          sources: ["mine_published"],
          mineShareStatus: "PUBLIC",
        },
      },
      MEISTER_STORE_ALL: {
        label: t("All", { ns: "Global" }),
        values: {
          sources: ["subscribable", "subscribed"],
        },
      },
      MEISTER_STORE_SUBSCRIBED: {
        label: t("Subscribed", { ns: "Templates" }),
        values: {
          sources: ["subscribed"],
        },
      },
    }),
    [t /*isShopEnabled,*/]
  );

  const sectionTitles = React.useMemo(
    (): Record<SectionValue, string> => ({
      ALL: t("All Available", { ns: "Templates" }),
      MY: t("My Templates", { ns: "Templates" }),
      MEISTER_STORE: t("Meister Store", { ns: "Templates" }),
    }),
    [t]
  );

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    activeView,
    setActiveView,
    queryBaseVariables,
    toRemoveParams,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQuoteList({
    activeViewStorageKey: "templates",
    activeViewInitialDesktopValue: "table",
    activeViewInitialMobileValue: "list",
    initialFilters: {},
  });

  const [quickFilter, setQuickFilter] = useStateWithUrlParam<QuickFilterValue>(
    "quickfilter",
    SECTION_TO_DEFAULT_QUICK_FILTER[section],
    toRemoveParams
  );

  const [templateFilters, setTemplateFilters] =
    useStateWithUrlParams<TemplateFiltersFormValues>(
      "templateFilters",
      templateFiltersDefaultValue,
      toRemoveParams
    );

  const activeQuickFilterOptions = React.useMemo<
    { label: string; value: QuickFilterValue }[]
  >(
    () =>
      Object.entries(quickFilterSettings)
        .filter(([, value]) => value?.visible !== false)
        .map(([key, value]) => ({
          value: key as QuickFilterValue,
          label: value.label,
        }))
        .filter(o => QUICK_FILTER_TO_SECTION_MAP[o.value] === section),
    [quickFilterSettings, section]
  );

  const variables: TemplateQuotesQueryVariables = {
    ...queryBaseVariables,
    sorting,
    searchTerm,
    filters: {
      ...quickFilterSettings[quickFilter].values,
      ...filters,
      createdAfter: filters?.createdAfter?.format("YYYY-MM-DD"),
      createdBefore: filters?.createdBefore?.format("YYYY-MM-DD"),
    },
  };

  const { customTags } = useOrganisationTags("TEMPLATE");

  const [selectedItems, setSelectedItems] = React.useState<
    TemplateQuotes_TemplateFragment[]
  >([]);
  const resetSelectedItems = React.useCallback(() => setSelectedItems([]), []);
  React.useEffect(() => {
    resetSelectedItems();
  }, [section, quickFilter, resetSelectedItems]);

  const { actionButtons } = useBulkActions(
    quickFilter,
    selectedItems,
    resetSelectedItems
  );

  // we need an inner formik state to not immediately apply filters on the list
  const formikProps = useFormik<TemplateFiltersFormValues>({
    initialValues: templateFilters,
    validationSchema: templateFiltersValidationSchema,
    validateOnMount: true,
    onSubmit: async newValues => {
      setTemplateFilters(newValues);
    },
  });

  useAutoSave({
    enableReinitialize: true,
    initialValues: templateFilters,
    formikProps,
  });

  const createButton = (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
      <CreateQuoteTemplateButton
        pathToDocList={pathToDocList}
        templateTypeId={
          templateFilters.templateTypeIds?.length === 1
            ? headOrThrow(templateFilters.templateTypeIds)
            : undefined
        }
      />
    </RestrictedByOrganisationPermissionWithDebug>
  );

  const menuButton = (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
      <MenuButton>
        <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
          <ImportTemplatesMenuItem />
        </RestrictedByOrganisationPermissionWithDebug>
      </MenuButton>
    </RestrictedByOrganisationPermissionWithDebug>
  );

  const inlineFilters = isMinTablet ? (
    <TemplateFilters
      formikProps={formikProps}
      templateSearchVariables={{
        searchTerm: variables.searchTerm,
        filters: variables.filters,
      }}
      viewType="buttons"
    />
  ) : undefined;

  const quickFilterTabs =
    activeQuickFilterOptions.length > 1 ? (
      <Tabs
        condensed
        useSelectOnMobile
        options={activeQuickFilterOptions}
        value={quickFilter}
        onChange={(newQuickFilter: QuickFilterValue) => {
          setQuickFilter(newQuickFilter);
        }}
      />
    ) : undefined;

  const filterButton =
    quickFilter === "ALL_AVAILABLE" ? (
      <ModalOpenButton
        Modal={TemplateQuotesFilterWithTemplateTypeModal}
        modalProps={{
          resetFilters,
          sorting,
          setSorting,
          filters,
          setFilters,
          tags: customTags,
          formikProps,
          templateSearchVariables: {
            searchTerm: variables.searchTerm,
            filters: variables.filters,
          },
        }}
      >
        <FilterButton />
      </ModalOpenButton>
    ) : (
      <ModalOpenButton
        Modal={TemplateQuotesFilterModal}
        modalProps={{
          resetFilters,
          sorting,
          setSorting,
          filters,
          setFilters,
          tags: customTags,
        }}
      >
        <FilterButton />
      </ModalOpenButton>
    );

  const switchViewButton = (
    <SwitchCollectionViewButton
      allowedViews={ALLOWED_VIEWS}
      activeView={activeView}
      setActiveView={setActiveView}
    />
  );

  const filterChips = canShowChips(filters, templateFilters) ? (
    <TemplatesQuoteFilterChips
      filters={filters}
      setFilters={setFilters}
      templateFilters={
        quickFilter === "ALL_AVAILABLE" ? templateFilters : undefined
      }
      setTemplateFilters={
        quickFilter === "ALL_AVAILABLE" ? setTemplateFilters : undefined
      }
      onReset={async () => {
        resetFilters();
        if (quickFilter === "ALL_AVAILABLE") {
          await formikProps.setValues(templateFiltersDefaultValue);
          await formikProps.submitForm();
        }
      }}
    />
  ) : undefined;

  const searchField = (
    <DebouncedSearchInput
      placeholder={t("Search", { ns: "Global" })}
      defaultValue={searchTerm}
      onChangeSearchTerm={newValue => setSearchTerm(newValue)}
      sx={
        quickFilter === "ALL_AVAILABLE"
          ? theme => ({
              flexGrow: 1,
              maxWidth: theme.layout.searchFieldWidth.lg,
            })
          : undefined
      }
    />
  );

  return (
    <Page
      // title={t("Quote Templates", {
      //   ns: "Templates",
      // })}
      title={sectionTitles[section]}
      subtitle={t("Document Templates", {
        ns: "Templates",
      })}
      submenuItems={submenuItems}
      action={actionButtons}
    >
      <PageContainer>
        {quickFilter === "ALL_AVAILABLE" ? (
          <ListHeader
            elementsOrder="separate-rows"
            CreateButton={createButton}
            MenuButton={menuButton}
            QuickFilter={inlineFilters}
            FilterButton={filterButton}
            SwitchViewButton={switchViewButton}
            FilterChips={filterChips}
            SearchField={searchField}
            mb={2}
          />
        ) : (
          <ListHeader
            CreateButton={createButton}
            MenuButton={menuButton}
            QuickFilter={quickFilterTabs}
            FilterButton={filterButton}
            SwitchViewButton={switchViewButton}
            FilterChips={filterChips}
            SearchField={searchField}
            mb={2}
          />
        )}

        {quickFilter === "ALL_AVAILABLE" ? (
          <TemplateQuotesSearchList
            variables={variables}
            templateFilters={templateFilters}
            section={section}
            pathToDocList={pathToDocList}
            quickFilter={quickFilter}
            activeView={activeView}
            sorting={sorting}
            setSorting={setSorting}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        ) : (
          <TemplateQuotesNormalList
            variables={variables}
            templateFilters={templateFilters}
            section={section}
            pathToDocList={pathToDocList}
            quickFilter={quickFilter}
            activeView={activeView}
            sorting={sorting}
            setSorting={setSorting}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        )}
      </PageContainer>
    </Page>
  );
};

function TemplateQuotesNormalList({
  variables,
  ...props
}: {
  variables: TemplateQuotesQueryVariables;
} & TemplateQuotesListProps) {
  const client = useApolloClient();
  const query = useTemplateQuotesQuery({
    client,
    variables,
  });

  const items = getDataOrNull(
    (query.data ?? query.previousData)?.quoteTemplates
  )?.edges.map(e => e.node);
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.quoteTemplates)
      ?.totalCount ?? 0;

  return (
    <TemplateQuotesList
      items={items ?? []}
      totalCount={totalCount}
      loading={query.loading}
      {...props}
    />
  );
}

function TemplateQuotesSearchList({
  variables,
  templateFilters,
  ...props
}: {
  variables: TemplateQuotesQueryVariables;
  templateFilters: TemplateFiltersFormValues;
} & TemplateQuotesListProps) {
  const client = useApolloClient();
  const query = useTemplateQuotesSearchQuery({
    client,
    variables: {
      ...variables,
      sorting: quoteTemplateSortingToSearchQuotesSorting(variables.sorting),
      filters: {
        ...variables.filters,
        ...templateFilters,
      },
    },
  });

  const items = getDataOrNull(
    (query.data ?? query.previousData)?.searchTemplates
  )?.edges.map(e => e.node);
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.searchTemplates)
      ?.totalCount ?? 0;

  return (
    <TemplateQuotesList
      items={items ?? []}
      totalCount={totalCount}
      loading={query.loading}
      templateFilters={templateFilters}
      {...props}
    />
  );
}

type TemplateQuotesListProps = {
  section: SectionValue;
  pathToDocList: string;
  quickFilter: QuickFilterValue;
  activeView: CollectionView;
  templateFilters: TemplateFiltersFormValues;
  sorting: OrganisationQuoteTemplatesSorting[];
  setSorting: (newSorting: OrganisationQuoteTemplatesSorting[]) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  selectedItems: TemplateQuotes_TemplateFragment[];
  setSelectedItems: React.Dispatch<
    React.SetStateAction<TemplateQuotes_TemplateFragment[]>
  >;
};

function TemplateQuotesList({
  items,
  totalCount,
  loading,
  section,
  pathToDocList,
  quickFilter,
  activeView,
  templateFilters,
  sorting,
  setSorting,
  paginationModel,
  setPaginationModel,
  selectedItems,
  setSelectedItems,
}: {
  items: TemplateQuotes_TemplateFragment[];
  totalCount: number;
  loading: boolean;
} & TemplateQuotesListProps) {
  const navigate = useNavigate();

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["Templates", "Global", "TemplatesSharing"]);
  const { getFormattedDate, getFormattedPrice } = useFormatting();
  const { getBoolLabel } = usePropertyHelpers();
  const { typeOfTemplatesLabels } = useTypeOfTemplates();
  const { projectTypeLabels } = useProjectTypes();
  const { spacing, sizeMeasureRef, columns } = useCardsNumber();

  const { getTemplateQuoteWarningIcon } = useTemplateQuoteWarnings();
  const { getTemplateQuoteStatusIcons } = useTemplateQuoteStatuses();

  function buildTemplateItemMeta(item: TemplateQuotes_TemplateFragment) {
    const statusIcons: React.ReactNode[] = [
      ...getTemplateQuoteStatusIcons(item),
      getTemplateQuoteWarningIcon(`${pathToDocList}/${item.id}`, item),
    ].filter(Boolean);

    const props = computeTemplateRelevantProperties({
      rootItemProps: item.rootItem.props2,
      templateTypeProps:
        item.implementsTemplateType?.templateType?.props2 ?? [],
    });

    const Info = hasTemplateTypeOrPropsInfo({
      templateTypeTitle: item.implementsTemplateType?.templateType.title,
      props,
    }) ? (
      <TemplateTypeAndPropsInfo
        templateTypeTitle={item.implementsTemplateType?.templateType.title}
        props={props}
      />
    ) : null;

    return { statusIcons, Info };
  }

  const stateStore = useDataGridStateStore("TemplateQuotesList");

  const { checkUpdateProtocol, updateVersionProtocolProcessRef } =
    useFetchUpdateProtocolEntries();

  const quoteTemplateColumns = React.useMemo(
    (): GridColDef<TemplateQuotes_TemplateFragment>[] => [
      {
        field: "title",
        headerName: t("Title", {
          ns: "Templates",
        }),
        flex: 2,
        minWidth: 100,
        renderCell: ({ row: template }) => template.title,
      },
      {
        field: "implementsTemplateType",
        headerName: t("Template type", {
          ns: "Templates",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.implementsTemplateType?.templateType?.title ?? "",
      },
      {
        field: "templateTypeProperties",
        headerName: t("Template type properties", {
          ns: "Templates",
        }),
        flex: 3,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row: template }) => {
          const props = computeTemplateRelevantProperties({
            rootItemProps: template.rootItem.props2,
            templateTypeProps:
              template.implementsTemplateType?.templateType?.props2 ?? [],
          });
          return (
            <PropertyValueChips
              highlightedKeys={templateFilters.properties?.map(p => p.key)}
              props={props}
              wrap={false}
            />
          );
        },
      },
      {
        field: "category",
        headerName: t("Template category", {
          ns: "Templates",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) => template.category?.name ?? "",
      },
      {
        field: "typeOfTemplate",
        headerName: t("Type of template", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.typeOfTemplate
            ? typeOfTemplatesLabels[template.typeOfTemplate]
            : "",
      },
      {
        field: "typeOfProject",
        headerName: t("Type of project", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.typeOfProject && template.typeOfProject.length > 0
            ? template.typeOfProject
                .map(top => projectTypeLabels[top])
                .join(", ")
            : "",
      },
      {
        field: "discountedPrice",
        headerName: t("Price", {
          ns: "Templates",
        }),
        width: 120,
        headerAlign: "right",
        align: "right",
        renderCell: ({ row: template }) =>
          getFormattedPrice(template.proposedCalculation?.priceNetTotal || 0),
      },
      {
        field: "tags",
        headerName: t("Tags", {
          ns: "Templates",
        }),
        flex: 1.5,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: template }) => (
          <TagChips tags={template.customTags} wrap={false} />
        ),
      },
      {
        field: "publishingStatus",
        headerName: t("Publishing", {
          ns: "Templates",
        }),
        flex: 0.75,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: template }) => (
          <TemplateQuotePublishingStatus
            doc={template}
            variant="body2"
            IconProps={{ fontSize: "small" }}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: t("Created", {
          ns: "Templates",
        }),
        width: 120,
        renderCell: ({ row: template }) => getFormattedDate(template.createdAt),
      },
      ...(section === "MEISTER_STORE" || section === "ALL"
        ? ([
            {
              field: "subscribed",
              headerName: t("Subscribed", {
                ns: "Templates",
              }),
              flex: 0.75,
              minWidth: 140,
              sortable: false,
              renderCell: ({ row: template }) => {
                if (
                  template.owningSystemOrganisationId === viewer.organisation.id
                ) {
                  return null;
                }

                return (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {!template.templateSharing?.subscribedToTemplate ? (
                      <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
                        <QuoteTemplateSubscribeButton
                          doc={template}
                          size="extra-small"
                          onAfterUpdate={async (opts, applyOverrides) => {
                            if (applyOverrides) {
                              await checkUpdateProtocol(opts);
                            }
                          }}
                        />
                      </RestrictedByOrganisationPermissionWithDebug>
                    ) : template.templateSharing?.subscribedToTemplate
                        ?.updateAvailable ? (
                      <Tooltip
                        title={t("New version available", { ns: "Templates" })}
                      >
                        <WarningIcon color="warning" />
                      </Tooltip>
                    ) : (
                      <CheckCircleIcon color="success" />
                    )}
                  </Stack>
                );
              },
            },
            {
              field: "hasOverridesDefined",
              headerName: t("Has overrides", { ns: "Templates" }),
              sortable: false,
              valueGetter(_value, template) {
                return template.hasOverridesDefined;
              },
              valueFormatter(value) {
                return getBoolLabel(value);
              },
            },
          ] as GridColDef<TemplateQuotes_TemplateFragment>[])
        : []),
      {
        field: "actions",
        headerName: "",
        sortable: false,
        maxWidth: 80,
        minWidth: 40,
        renderCell: ({ row: template }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
              <IconButton
                component={Link}
                to={
                  buildDocPath(
                    `${pathToDocList}/${template.id}`,
                    template.resolvedAsReadModelVersionNumber ?? null
                  ) + "/troubleshoot"
                }
                onClick={event => {
                  event.stopPropagation();
                }}
                color={"primary"}
                sx={{ marginLeft: "-10px" }}
              >
                <BugReportIcon />
              </IconButton>
            </RestrictedByOrganisationPermissionWithDebug>
            {/*<RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
              {template.resolvedAsReadModelVersionNumber ? (
                <ShowTemplateVariantProtocolButton
                  templateId={template.id}
                  versionNumber={template.resolvedAsReadModelVersionNumber}
                />
              ) : null}
            </RestrictedByOrganisationPermissionWithDebug>*/}
          </Stack>
        ),
      },
    ],
    [
      t,
      section,
      templateFilters.properties,
      typeOfTemplatesLabels,
      projectTypeLabels,
      getFormattedPrice,
      getFormattedDate,
      viewer.organisation.id,
      checkUpdateProtocol,
      getBoolLabel,
      pathToDocList,
    ]
  );

  if (!loading && items.length === 0) {
    if (quickFilter === "ALL_AVAILABLE")
      return <EmptyScreen slug="templates-quotes" />;
  }

  switch (activeView) {
    case "table":
      return (
        <>
          <DataGrid<TemplateQuotes_TemplateFragment>
            stateStore={stateStore}
            loading={loading}
            hideFooter={totalCount === 0}
            columns={quoteTemplateColumns}
            rows={items}
            sortModel={sorting.map(s => ({
              field: s.column,
              sort: s.direction,
            }))}
            onSortModelChange={newModel => {
              setSorting(
                newModel.map(({ field, sort }) => ({
                  column: field as OrganisationQuoteTemplatesSortBy,
                  direction: sort ?? "asc",
                }))
              );
            }}
            onRowClick={({ row: template }) => {
              return navigate(
                buildDocPath(
                  `${pathToDocList}/${template.id}`,
                  template.resolvedAsReadModelVersionNumber ?? null
                )
              );
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={newPaginationModel => {
              setPaginationModel(newPaginationModel);
            }}
            pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
            rowCount={totalCount}
            checkboxSelection
            onRowSelectionModelChange={model => {
              const toAdd = model
                .filter(id => !selectedItems.find(item => item.id === id))
                .map(id => items.find(item => item.id === id))
                .filter(item => item) as TemplateQuotes_TemplateFragment[]; // FIXME
              setSelectedItems([
                ...selectedItems.filter(item => model.includes(item.id)),
                ...toAdd,
              ]);
            }}
            rowSelectionModel={selectedItems.map(item => item.id)}
            localeText={{
              noRowsLabel: t("There are no quote templates.", {
                ns: "Templates",
              }),
              MuiTablePagination: {
                labelRowsPerPage: t("Rows per page", {
                  ns: "Global",
                }),
              },
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  typeOfTemplate: false,
                  typeOfProject: false,
                },
              },
            }}
          />
          <ModalOpenProcess
            ref={updateVersionProtocolProcessRef}
            Modal={ShowTemplateVariantProtocolModal}
            modalProps={{
              pathToDocList,
              toVersionNumber: 0,
              entries: [],
            }}
          />
        </>
      );
    case "list": {
      if (loading) {
        return <LoadingSpinner />;
      }
      if (!items.length) {
        return (
          <Box
            display={"flex"}
            justifyContent="center"
            alignItems="center"
            p={2}
          >
            <Typography color="grey.600" variant="body2">
              {t("There are no quote templates.", { ns: "Templates" })}
            </Typography>
          </Box>
        );
      }
      return (
        <Stack spacing={1}>
          {items.map(item => {
            const { statusIcons, Info } = buildTemplateItemMeta(item);

            return (
              <MediaListItem
                key={item.id}
                // @ts-ignore
                component={Link}
                to={buildDocPath(
                  `${pathToDocList}/${item.id}`,
                  item.resolvedAsReadModelVersionNumber ?? null
                )}
                title={item.title}
                description={item.description}
                price={getFormattedPrice(
                  item.proposedCalculation?.priceNetTotal || 0
                )}
                imageUrl={item.shopCatalogProductImage?.url}
                Tags={
                  item.customTags.length > 0 ? (
                    <TagChips tags={item.customTags} wrap={false} />
                  ) : null
                }
                Info={Info}
                StatusIcons={statusIcons}
              />
            );
          })}
          <TablePagination
            component="div"
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(_event, newPage) => {
              setPaginationModel({
                pageSize: paginationModel.pageSize,
                page: newPage,
              });
            }}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event => {
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              });
            }}
            labelRowsPerPage={t("Per page:", {
              ns: "Global",
            })}
          />
        </Stack>
      );
    }
    case "gallery": {
      return (
        <Stack spacing={1} ref={sizeMeasureRef}>
          {loading ? (
            <LoadingSpinner />
          ) : !items.length ? (
            <Box
              display={"flex"}
              justifyContent="center"
              alignItems="center"
              p={2}
            >
              <Typography color="grey.600" variant="body2">
                {t("There are no quote templates.", { ns: "Templates" })}
              </Typography>
            </Box>
          ) : (
            <>
              <Box overflow="auto" flex={1}>
                <Grid container spacing={spacing}>
                  {items.map(item => {
                    const { statusIcons, Info } = buildTemplateItemMeta(item);

                    return (
                      <Grid key={item.id} size={columns}>
                        <MediaCardItem
                          key={item.id}
                          component={Link}
                          to={buildDocPath(
                            `${pathToDocList}/${item.id}`,
                            item.resolvedAsReadModelVersionNumber ?? null
                          )}
                          title={item.title}
                          description={item.description}
                          price={getFormattedPrice(
                            item.proposedCalculation?.priceNetTotal || 0
                          )}
                          imageUrl={item.shopCatalogProductImage?.url}
                          Tags={
                            item.customTags.length > 0 ? (
                              <TagChips tags={item.customTags} wrap={true} />
                            ) : null
                          }
                          Info={Info}
                          StatusIcons={statusIcons}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              <TablePagination
                component="div"
                count={totalCount}
                page={paginationModel.page}
                onPageChange={(_event, newPage) => {
                  setPaginationModel({
                    pageSize: paginationModel.pageSize,
                    page: newPage,
                  });
                }}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={event => {
                  setPaginationModel({
                    pageSize: parseInt(event.target.value, 10),
                    page: 0,
                  });
                }}
                labelRowsPerPage={t("Per page:", {
                  ns: "Global",
                })}
              />
            </>
          )}
        </Stack>
      );
    }
  }
}

type Action =
  | "delete"
  | "duplicate"
  | "publish"
  | "share"
  | "subscribe"
  | "unsubscribe"
  | "export"
  | "sync_products"
  | "assign";

function useBulkActions(
  quickFilter: QuickFilterValue,
  selectedItems: TemplateQuotes_TemplateFragment[],
  resetSelectedItems: () => void
) {
  const viewer = useUserData().currentUser!;

  const { t } = useTranslate([
    "Templates",
    "TemplatesSharing",
    "TemplatesPublishing",
  ]);

  const availableActions = getAvailableActionsForQuickFilter(
    quickFilter
  ).filter(action => doesAnyItemSupportAction(viewer, action, selectedItems));

  const buttons: Record<Action, React.ReactNode> = {
    duplicate: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"duplicate"}
        permission="MANAGE_TEMPLATE"
      >
        <DuplicateItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    publish: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"publish"}
        permission="MANAGE_TEMPLATE"
      >
        <PublishItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    share: (
      <RestrictedByCapabilityWithDebug
        key={"share"}
        capability="TEMPLATE_SHARING"
      >
        <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
          <ShareItemsButton
            items={selectedItems}
            handleComplete={resetSelectedItems}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      </RestrictedByCapabilityWithDebug>
    ),
    subscribe: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"subscribe"}
        permission="SHARE_TEMPLATE"
      >
        <SubscribeItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    unsubscribe: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"unsubscribe"}
        permission="SHARE_TEMPLATE"
      >
        <UnsubscribeItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    export: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"export"}
        permission="MANAGE_TEMPLATE"
      >
        <ExportButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    sync_products: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"sync_products"}
        permission="MANAGE_TEMPLATE"
      >
        <SyncItemsProductsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    delete: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"delete"}
        permission="MANAGE_TEMPLATE"
      >
        <DeleteItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    assign: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"assign"}
        permission="MANAGE_TEMPLATE_SETTINGS"
      >
        <AssignItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
  };

  const actionButtons =
    selectedItems.length > 0
      ? availableActions.length === 0
        ? t("No actions available", {
            ns: "Templates",
          })
        : availableActions.map(action => buttons[action])
      : [];

  return { actionButtons };
}

function getAvailableActionsForQuickFilter(
  quickFilter: QuickFilterValue
): Action[] {
  const quickFilterAvailableActions: Record<QuickFilterValue, Action[]> = {
    ALL_AVAILABLE: ["export"],
    MY_ALL: [
      "duplicate",
      "publish",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_DRAFT: [
      "duplicate",
      "publish",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_PRIVATE: [
      "duplicate",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_PROTECTED: [
      "duplicate",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_PUBLIC: [
      "duplicate",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MEISTER_STORE_ALL: ["subscribe", "unsubscribe"],
    MEISTER_STORE_SUBSCRIBED: ["unsubscribe"],
  };

  return quickFilterAvailableActions[quickFilter];
}

function doesAnyItemSupportAction(
  viewer: ViewerShapeFragment,
  action: Action,
  items: TemplateQuotes_TemplateFragment[]
) {
  switch (action) {
    case "duplicate":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "publish":
      return items.some(item => !item.hasAnyPublishedVersion);
    case "share":
      return items.some(
        item =>
          item.templateSharing?.sharedPublicly === false &&
          item.hasAnyPublishedVersion
      );
    case "subscribe":
      return items.some(item => !item.templateSharing?.subscribedToTemplate);
    case "unsubscribe":
      return items.some(item => !!item.templateSharing?.subscribedToTemplate);
    case "export":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "sync_products":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "delete":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "assign":
      return true;
    default:
      return assertNever(action);
  }
}

function AssignItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete: () => void;
}) {
  const { t } = useTranslate("Templates");
  const { enqueueSnackbar } = useSnackbar();

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const client = useApolloClient();
  const [implementsTemplateType] = useImplementsTemplateTypeMutation({
    client,
  });
  const [modifyQuoteTemplate] = useModifyQuoteTemplateMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationTags,
      // ...(refetchQueries ?? []),
    ],
    awaitRefetchQueries: true,
  });

  return (
    <SplitButton
      buttons={[
        <ModalOpenButton
          Modal={TemplateTypesSearchModal}
          modalProps={{
            handleSelectTemplateType: async (templateType, handleClose) => {
              await Promise.allSettled(
                items.map(async item => {
                  await implementsTemplateType({
                    variables: {
                      input: {
                        templateId: item.id,
                        templateTypeId: templateType.id,
                        templateTypePropertiesStrategy:
                          "keepPropertiesWithMerging",
                      },
                    },
                  });
                })
              ).then(results => {
                const [success, fail] = partition(
                  results,
                  result => result.status === "fulfilled"
                );

                enqueueSnackbar(
                  t(
                    "{count, plural, =0 {No templates} one {One template} other {# templates}} changed",
                    {
                      ns: "Templates",
                      count: success.length,
                    }
                  )
                );
                if (fail.length > 0) {
                  enqueueSnackbar(
                    t(
                      "{count, plural, one {One template} other {# templates}} failed to be changed",
                      {
                        ns: "Templates",
                        count: fail.length,
                      }
                    ),
                    { variant: "error" }
                  );
                }
              });

              handleClose();
              handleComplete();
            },
          }}
        >
          <Button>
            {t("Assign template type", {
              ns: "Templates",
            })}
          </Button>
        </ModalOpenButton>,
        <ModalOpenButton
          Modal={CategorySearchModal}
          modalProps={{
            handleSelectCategory: async (category, handleClose) => {
              await Promise.allSettled(
                items.map(async item => {
                  await modifyQuoteTemplate({
                    variables: {
                      input: {
                        quoteTemplateId: item.id,
                        values: {
                          category: category.id,
                        },
                      },
                    },
                  });
                })
              ).then(results => {
                const [success, fail] = partition(
                  results,
                  result => result.status === "fulfilled"
                );

                enqueueSnackbar(
                  t(
                    "{count, plural, =0 {No templates} one {One template} other {# templates}} changed",
                    {
                      ns: "Templates",
                      count: success.length,
                    }
                  )
                );
                if (fail.length > 0) {
                  enqueueSnackbar(
                    t(
                      "{count, plural, one {One template} other {# templates}} failed to be changed",
                      {
                        ns: "Templates",
                        count: fail.length,
                      }
                    ),
                    { variant: "error" }
                  );
                }
              });

              handleClose();
              handleComplete();
            },
          }}
        >
          <Button>
            {t("Assign template category", {
              ns: "Templates",
            })}
          </Button>
        </ModalOpenButton>,
        <ModalOpenButton
          Modal={SelectTagsModal}
          modalProps={{
            handleComplete: async (tags, handleClose) => {
              await Promise.allSettled(
                items.map(async item => {
                  await modifyQuoteTemplate({
                    variables: {
                      input: {
                        quoteTemplateId: item.id,
                        values: {
                          tags: Array.from(
                            new Set(item.customTags.concat(tags))
                          ),
                        },
                      },
                    },
                  });
                })
              ).then(results => {
                const [success, fail] = partition(
                  results,
                  result => result.status === "fulfilled"
                );

                enqueueSnackbar(
                  t(
                    "{count, plural, =0 {No templates} one {One template} other {# templates}} changed",
                    {
                      ns: "Templates",
                      count: success.length,
                    }
                  )
                );
                if (fail.length > 0) {
                  enqueueSnackbar(
                    t(
                      "{count, plural, one {One template} other {# templates}} failed to be changed",
                      {
                        ns: "Templates",
                        count: fail.length,
                      }
                    ),
                    { variant: "error" }
                  );
                }
              });

              handleClose();
              handleComplete();
            },
          }}
        >
          <Button>{t("Add tags", { ns: "Templates" })}</Button>
        </ModalOpenButton>,
      ]}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
    />
  );
}

function SelectTagsModal({
  handleClose,
  handleComplete,
}: {
  handleClose: () => void;
  handleComplete: (tags: Array<string>, handleClose: () => void) => void;
}) {
  const { t } = useTranslate(["Templates", "Global"]);

  const { customTags } = useOrganisationTags("TEMPLATE");

  function handleSubmit(values: { tags: Array<{ value: string }> }) {
    handleComplete(
      values.tags.map(tag => tag.value),
      handleClose
    );
  }

  const formId = React.useId();

  return (
    <Formik initialValues={{ tags: [] }} onSubmit={handleSubmit}>
      <Modal
        handleClose={handleClose}
        title={t("Add tags", { ns: "Templates" })}
        actionButtons={[
          {
            label: t("Cancel", { ns: "Global" }),
            handleClick: () => handleClose(),
            buttonProps: {
              variant: "text",
            },
          },
          {
            label: t("Save", { ns: "Global" }),
            buttonProps: {
              type: "submit",
              form: formId,
            },
          },
        ]}
      >
        <Form id={formId}>
          <TagsField
            name="tags"
            options={customTags}
            inputLabel={t("Tags", { ns: "Templates" })}
          />
        </Form>
      </Modal>
    </Formik>
  );
}

function ExportButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { t } = useTranslate("Templates");
  const [exportTemplates, { isLoading }] = useJsonExport();
  const viewer = useUserData().currentUser!;

  return (
    <Button
      variant="contained"
      startIcon={
        <Icon>
          <FontAwesomeIcon icon={faFileExport} />
        </Icon>
      }
      onClick={async () => {
        const { downloadUrl, filename } = await exportTemplates(
          items
            .filter(
              item => item.owningSystemOrganisationId === viewer.organisation.id
            )
            .map(item => ({
              docId: item.id,
              versionNumber: item.resolvedAsReadModelVersionNumber ?? null,
            }))
        );
        download(downloadUrl, filename);
        if (handleComplete) handleComplete();
      }}
      loading={isLoading}
    >
      {t("Export templates")}
    </Button>
  );
}

function DuplicateItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Quote", "Global"]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [duplicateQuoteTemplateMutation, { loading }] =
    useDuplicateQuoteTemplateMutation({
      client,
    });

  return (
    <BulkActionButton
      text={t("Duplicate", {
        ns: "Quote",
      })}
      action={async item => {
        await duplicateQuoteTemplateMutation({
          variables: {
            input: {
              title: getNameWithCopy(
                item.title,
                t("copy", {
                  ns: "Global",
                })
              ),
              quoteTemplateId: item.id,
            },
          },
        });
      }}
      items={items.filter(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      )}
      startIcon={<ControlPointDuplicateIcon />}
      loading={loading}
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} duplicated",
            {
              ns: "Quote",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}
function PublishItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesSharing"]);

  const client = useApolloClient();
  const [publish, { loading }] = usePublishQuoteTemplateMutation({
    client,
  });

  return (
    <BulkActionButton
      text={t("Publish", {
        ns: "TemplatesSharing",
      })}
      action={async item => {
        await publish({ variables: { input: { docId: item.id } } });
      }}
      items={items.filter(item => !item.hasAnyPublishedVersion)}
      startIcon={<PublishIcon />}
      loading={loading}
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} published",
            {
              ns: "TemplatesSharing",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}

function SubscribeItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesPublishing", "Global"]);

  const client = useApolloClient();
  const [fetch, query] = useSubscribeToQuoteTemplate_HasOverridesLazyQuery({
    client,
  });
  const [subscribe, { loading }] = useSubscribeToQuoteTemplateMutation({
    client,
  });
  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);

  return (
    <>
      <BulkActionButton
        text={t("Subscribe", { ns: "TemplatesPublishing" })}
        action={async item => {
          assert(item.templateSharing, "expected templateSharing");
          assert(
            !item.templateSharing.subscribedToTemplate,
            "already subscribed"
          );
          assert(
            item.templateSharing.latestSharedVersionNumber,
            "expected doc.templateSharing.latestSharedVersionNumber"
          );

          let applyOverrides = false;
          const result = await fetch({ variables: { templateId: item.id } });
          assert(
            result?.data?.quoteTemplateHasOverrides,
            "expected quoteTemplateHasOverrides result"
          );
          const latestVersionWithOverrides = getDataOrNull(
            result?.data?.quoteTemplateHasOverrides
          )?.version;

          if (
            latestVersionWithOverrides &&
            latestVersionWithOverrides <
              item.templateSharing.latestSharedVersionNumber
          ) {
            const result = await confirmProcessRef.current?.startConfirmProcess(
              {
                title: t("Applying overrides", { ns: "TemplatesPublishing" }),
                text: t(
                  "You have overrides from a previous version of a template: {title}. Do you want to apply them to new version?",
                  { ns: "TemplatesPublishing", title: item.title }
                ),
                confirmButtonLabel: t("Yes", { ns: "Global" }),
                cancelButtonLabel: t("No", { ns: "Global" }),
              }
            );
            if (result) {
              applyOverrides = true;
            }
          }

          await subscribe({
            variables: {
              input: {
                quoteTemplateId: item.id,
                versionNumber: item.templateSharing.latestSharedVersionNumber,
                applyOverrides,
              },
            },
          });
        }}
        items={items.filter(
          item =>
            item.templateSharing && !item.templateSharing.subscribedToTemplate
        )}
        startIcon={<BookmarkAddIcon />}
        loading={loading || query.loading}
        handleComplete={itemCount => {
          if (handleComplete) handleComplete();
          enqueueSnackbar(
            t(
              "{count, plural, =0 {No templates} one {One template} other {# templates}} subscribed",
              {
                ns: "TemplatesPublishing",
                count: itemCount,
              }
            )
          );
        }}
        onFinalized={async () => {
          await client.refetchQueries({
            include: [namedOperations.Query.TemplateQuotes],
          });
        }}
      />
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
}
function UnsubscribeItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesPublishing"]);

  const client = useApolloClient();
  const [unsubscribe, { loading }] = useUnsubscribeFromQuoteTemplateMutation({
    client,
  });
  return (
    <BulkActionButton
      text={t("Unsubscribe", {
        ns: "TemplatesPublishing",
      })}
      action={async item => {
        await unsubscribe({
          variables: { input: { quoteTemplateId: item.id } },
        });
      }}
      items={items.filter(item => !!item.templateSharing?.subscribedToTemplate)}
      startIcon={<BookmarkRemoveIcon />}
      loading={loading}
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} unsubscribed",
            {
              ns: "TemplatesPublishing",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}

interface BulkActionProcessProps<Item extends { id: string; title: string }> {
  items: Item[];
  action: (item: Item) => Promise<void>;
  handleComplete?: (itemCount: number) => void;
  sequentially?: boolean;
  onFinalized?(): void | Promise<void>;
}

function useBulkActionProcess<Item extends { id: string; title: string }>({
  items,
  action,
  handleComplete,
  sequentially,
  onFinalized,
}: BulkActionProcessProps<Item>) {
  const { enqueueSnackbar } = useSnackbar();
  return async () => {
    try {
      if (sequentially) {
        for (const item of items) {
          await action(item);
        }
      } else {
        await Promise.all(items.map(action));
      }

      if (handleComplete) handleComplete(items.length);
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      onFinalized?.();
    }
  };
}

function BulkActionButton<Item extends { id: string; title: string }>({
  text,
  items,
  action,
  handleComplete,
  sequentially,
  onFinalized,
  ...props
}: {
  text: string;
} & BulkActionProcessProps<Item> &
  Omit<ButtonProps, "action">) {
  const handleProcess = useBulkActionProcess({
    items,
    action,
    handleComplete,
    sequentially,
    onFinalized,
  });
  return (
    <Button variant="contained" onClick={handleProcess} {...props}>
      {text}
    </Button>
  );
}

function ShareItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesSharing"]);

  return (
    <ModalOpenButton
      Modal={QuoteTemplatesShareModal}
      modalProps={{
        templates: items.filter(
          item =>
            !item.templateSharing?.sharedPublicly && item.hasAnyPublishedVersion
        ),
        handleComplete: count => {
          handleComplete?.();
          enqueueSnackbar(
            t(
              "{count, plural, =0 {No templates} one {One template} other {# templates}} sharing updated",
              {
                ns: "TemplatesSharing",
                count,
              }
            )
          );
        },
      }}
    >
      <Button startIcon={<GroupAddIcon />} variant="contained" color="primary">
        {t("Share", {
          ns: "TemplatesSharing",
        })}
      </Button>
    </ModalOpenButton>
  );
}
function SyncItemsProductsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void | Promise<void>;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "Templates",
    "TemplatesSharing",
    "TemplatesPublishing",
  ]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();

  const [templateBulkUpdateProducts, { loading }] =
    useTemplateBulkUpdateProductsMutation({
      client,
    });

  const handleUpdateShare = useQuoteTemplateUpdateShare({});
  const handlePublish = useQuoteTemplatePublish();

  const flagsRef = React.useRef<SyncProductsFlags>({
    publishLatestVersions: false,
    publishAlsoToMeisterStore: false,
  });

  const countsRef = React.useRef({
    publishedNumber: 0,
    publishedToMeisterStoreNumber: 0,
  });

  const handleProcess = useBulkActionProcess({
    items: items.filter(
      item => item.owningSystemOrganisationId === viewer.organisation.id
    ),
    action: async item => {
      const result = await templateBulkUpdateProducts({
        variables: {
          input: {
            docId: item.id,
          },
        },
      });

      // 1. Additionally, publish latest version
      if (
        flagsRef.current.publishLatestVersions &&
        result.data?.templateBulkUpdateProducts.doc.hasAnyPublishedVersion &&
        result.data?.templateBulkUpdateProducts.doc
          .containsUnpublishedChanged &&
        // has changes in doc after product prices update
        result.data?.templateBulkUpdateProducts.changedItems.length
      ) {
        const { template, newVersionNumber } = await handlePublish(item.id);
        countsRef.current.publishedNumber++;

        // 2. Additionally, update sharing of latest version to Meister Store
        if (
          template &&
          flagsRef.current.publishAlsoToMeisterStore &&
          canUpdateShare(template, newVersionNumber)
        ) {
          await handleUpdateShare(template, newVersionNumber);
          countsRef.current.publishedToMeisterStoreNumber++;
        }
      }
    },
    handleComplete: async itemCount => {
      await handleComplete?.();
      if (itemCount > 0) {
        enqueueSnackbar(
          t(
            "Synced products for {count, plural, =0 {no template} one {one template} other {# templates}}",
            {
              ns: "Templates",
              count: itemCount,
            }
          )
        );
      }
    },
    onFinalized: async () => {
      if (countsRef.current.publishedNumber > 0) {
        enqueueSnackbar(
          t(
            "New version of {count, plural, =0 {no template} one {one template} other {# templates}} published",
            {
              ns: "TemplatesSharing",
              count: countsRef.current.publishedNumber,
            }
          )
        );
      }
      if (countsRef.current.publishedToMeisterStoreNumber > 0) {
        enqueueSnackbar(
          t(
            "New version of {count, plural, =0 {no template} one {one template} other {# templates}} shared to Meister Store",
            {
              ns: "TemplatesSharing",
              count: countsRef.current.publishedToMeisterStoreNumber,
            }
          )
        );
      }
      await client.refetchQueries({
        include: [namedOperations.Query.TemplateQuotes],
      });
    },
  });

  return (
    <ModalOpenButton
      Modal={SyncProductsConfirmModal}
      modalProps={{
        handleConfirm: async flags => {
          // save flags to ref
          flagsRef.current = flags;
          countsRef.current = {
            publishedNumber: 0,
            publishedToMeisterStoreNumber: 0,
          };
          // start processing
          await handleProcess();
        },
      }}
    >
      <Button variant="contained" startIcon={<SyncIcon />} loading={loading}>
        {t("Sync products", { ns: "Templates" })}
      </Button>
    </ModalOpenButton>
  );
}

type SyncProductsFlags = {
  publishLatestVersions: boolean;
  publishAlsoToMeisterStore: boolean;
};

function SyncProductsConfirmModal({
  handleClose,
  handleConfirm,
}: {
  handleClose: () => void;
  handleConfirm: (flags: SyncProductsFlags) => void | Promise<void>;
}) {
  const { t } = useTranslate(["Templates", "Global", "TemplatesPublishing"]);
  const [flags, setFlags] = React.useState<SyncProductsFlags>({
    publishLatestVersions: false,
    publishAlsoToMeisterStore: false,
  });
  return (
    <ConfirmModal
      handleClose={handleClose}
      handleConfirm={async () => {
        await handleConfirm(flags);
      }}
      title={t("Sync products", { ns: "Templates" })}
      confirmButtonLabel={t("Continue", { ns: "Global" })}
      content={() => (
        <Stack direction="column" spacing={1}>
          <Typography variant="body1">
            {t("Do you want to publish new version of affected templates?", {
              ns: "TemplatesPublishing",
            })}
          </Typography>

          <FormControl variant="standard">
            <FormControlLabel
              control={
                <Checkbox
                  checked={flags.publishLatestVersions}
                  indeterminate={false}
                  onChange={(event, checked) => {
                    setFlags(f => ({ ...f, publishLatestVersions: checked }));
                  }}
                  size="small"
                />
              }
              label={t("Yes, publish new version", {
                ns: "TemplatesPublishing",
              })}
            />
          </FormControl>
          <FormControl variant="standard">
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    flags.publishLatestVersions &&
                    flags.publishAlsoToMeisterStore
                  }
                  disabled={!flags.publishLatestVersions}
                  indeterminate={false}
                  onChange={(event, checked) => {
                    setFlags(f => ({
                      ...f,
                      publishAlsoToMeisterStore: checked,
                    }));
                  }}
                  size="small"
                />
              }
              label={t("Yes, and also publish to Meister Store", {
                ns: "TemplatesPublishing",
              })}
            />
          </FormControl>
        </Stack>
      )}
    />
  );
}

function DeleteItemsButton({
  items: itemsInitial,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Templates"]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [deleteTemplate, { loading }] = useDeleteQuoteTemplateMutation({
    client,
  });

  const items = React.useMemo(() => {
    const dependencyGraph = new DepGraph<string>({ circular: false });

    const filterdItems = itemsInitial
      .filter(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      )
      .filter(item => !(item.hasAnyPublishedVersion && !!item.templateSharing));

    for (const item of filterdItems) {
      if (!dependencyGraph.hasNode(item.id)) {
        dependencyGraph.addNode(item.id);
      }
      item.linkedTemplateIdsByThis.forEach(id => {
        if (filterdItems.some(item => item.id === id)) {
          if (!dependencyGraph.hasNode(id)) {
            dependencyGraph.addNode(id);
          }
          dependencyGraph.addDependency(id, item.id);
        }
      });
    }

    const orderedItemIds = dependencyGraph.overallOrder();

    const itemsById = keyBy(itemsInitial, i => i.id);

    return orderedItemIds.map(id => throwIfUndefined(itemsById[id]));
  }, [itemsInitial, viewer.organisation.id]);

  return (
    <BulkActionButton
      text={t("Delete Templates", {
        ns: "Templates",
      })}
      action={async item => {
        await deleteTemplate({
          variables: { input: { quoteTemplateId: item.id } },
        });
      }}
      items={items}
      startIcon={<DeleteIcon />}
      loading={loading}
      sequentially
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} deleted",
            {
              ns: "Templates",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}
